import { Mail } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { AuthState } from '@/lib/schemas/auth';

import {
  AuthStatusButton,
  AuthStatusCard,
  AuthStatusError,
  AuthStatusLink,
  type AuthStatusTone,
} from './AuthStatusCard';
import { useOneShotAction } from './useOneShotAction';

/**
 * `lead` exists because one of these screens is not "we just sent you an
 * email". `email-unconfirmed` is reached from SIGN-IN: the link went out
 * whenever the person signed up, possibly days ago, and claiming otherwise
 * sends them hunting through their inbox for a mail that is not there. Its tone
 * differs for the same reason - it is a blocked sign-in, not a success.
 */
const COPY: Record<
  Exclude<AuthState, 'form'>,
  { title: string; lead: string; tail: string; tone: AuthStatusTone }
> = {
  'magic-link-sent': {
    title: 'Check your email',
    lead: 'We sent an email to',
    tail: 'with a link that signs you in. You can close this tab.',
    tone: 'success',
  },
  'registration-success': {
    title: 'Confirm your email',
    lead: 'We sent an email to',
    tail: 'to finish setting up your account. The link expires in 24 hours.',
    tone: 'success',
  },
  'email-unconfirmed': {
    title: 'Confirm your email to sign in',
    lead: 'This account is not confirmed yet. We sent a link to',
    tail: 'when you signed up - resend it if you cannot find it.',
    tone: 'warning',
  },
  'reset-email-sent': {
    title: 'Check your email',
    lead: 'We sent an email to',
    tail: 'with a link to set a new password. It expires in 1 hour.',
    tone: 'success',
  },
};

interface AuthSuccessProps {
  state: AuthState;
  email: string;
  /** Returns to the form these screens were reached from. */
  onBack: () => void;
}

/**
 * The "check your email" screen, for all four cases.
 *
 * Two things the first version did not do:
 *
 * 1. **The address is the loudest thing on the screen.** It is the one piece of
 *    information the user can actually check, and a typo in it is the single
 *    most common reason the mail never arrives. It used to sit inline in a
 *    muted sentence at the same weight as everything around it.
 * 2. **Resend.** There was no resend path anywhere in the codebase. If the mail
 *    landed in spam or the link expired, the user's only route was to start
 *    over - and on the confirm-email case they could not even do that, because
 *    the account already existed.
 *
 * Resend is offered for the two confirm-your-email states. The magic-link and
 * reset states are reached from a form that is one click away behind "Use a
 * different address", so re-submitting it is both the resend and the
 * correction; a dedicated resend button there would just be a second path to
 * the same request. `email-unconfirmed` is the opposite case - it is reached
 * from sign-in, the account already exists, and re-submitting the sign-in form
 * only fails the same way again. Resend is the only way forward, which is why
 * this screen exists rather than an inline error on the form.
 */
export function AuthSuccess({ state, email, onBack }: AuthSuccessProps) {
  const { resendConfirmation } = useAuth();
  const resend = useOneShotAction(() => resendConfirmation(email));

  const copy =
    COPY[state as Exclude<AuthState, 'form'>] ?? COPY['magic-link-sent'];
  const canResend =
    state === 'registration-success' || state === 'email-unconfirmed';

  return (
    <AuthStatusCard
      tone={copy.tone}
      icon={<Mail className='h-5 w-5' />}
      title={copy.title}
      body={
        <>
          {copy.lead}{' '}
          <span className='font-medium text-foreground'>{email}</span>{' '}
          {copy.tail}
        </>
      }
    >
      {canResend && (
        <AuthStatusButton
          variant='outline'
          pending={resend.isPending}
          disabled={resend.isDone}
          onClick={resend.run}
        >
          {resend.isDone ? 'Email sent' : 'Resend email'}
        </AuthStatusButton>
      )}
      <AuthStatusLink onClick={onBack}>Use a different address</AuthStatusLink>
      {resend.error && <AuthStatusError message={resend.error} />}
    </AuthStatusCard>
  );
}
